import React, { useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "@/lib/auth";
import { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label as UILabel } from "@/components/ui/label";
import { toast } from "sonner";

export default function Login() {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const { login, user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const from = location.state?.from?.pathname || "/";

  React.useEffect(() => {
    if (user) navigate(from, { replace: true });
  }, [user, from, navigate]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      await login(email.trim().toLowerCase(), password);
      toast.success("Welcome back");
      navigate(from, { replace: true });
    } catch (err) {
      const msg = formatApiError(err.response?.data?.detail) || err.message;
      setError(msg);
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex bg-slate-50">
      <div className="hidden lg:flex flex-col justify-between w-1/2 bg-slate-900 text-white p-12 sidebar-noise relative overflow-hidden">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded bg-emerald-400 text-slate-900 flex items-center justify-center font-heading font-bold text-lg">E</div>
          <div>
            <div className="font-heading text-xl font-bold">EliteOS</div>
            <div className="text-[11px] uppercase tracking-widest text-slate-400">Sales · Accounts · Finance</div>
          </div>
        </div>
        <div className="max-w-md">
          <div className="text-[11px] uppercase tracking-[0.22em] text-emerald-400 mb-3">One finance workspace</div>
          <h2 className="font-heading text-4xl leading-tight font-bold mb-4">
            Trace every ringgit from purchase to reconciled collection.
          </h2>
          <p className="text-sm text-slate-400 leading-relaxed">
            Pro-formas, verified receipts, agency commissions, SST prep and revenue recognition — auditable end-to-end,
            so a two-person team can run the entire operation.
          </p>
        </div>
        <div className="text-[11px] uppercase tracking-widest text-slate-500">
          AI Elite Mastery · Internal Operations
        </div>
      </div>

      <div className="flex-1 flex items-center justify-center px-6 py-12">
        <form onSubmit={handleSubmit} className="w-full max-w-sm" data-testid="login-form">
          <div className="mb-8">
            <div className="text-[10px] uppercase tracking-[0.2em] text-slate-500 mb-2">Secure sign in</div>
            <h1 className="font-heading text-3xl font-bold text-slate-900">Welcome back</h1>
            <p className="mt-2 text-sm text-slate-500">Sign in with your EliteOS credentials to continue.</p>
          </div>

          <div className="space-y-4">
            <div className="space-y-2">
              <UILabel htmlFor="email" className="text-xs uppercase tracking-wider text-slate-600">Email</UILabel>
              <Input
                id="email"
                type="email"
                autoComplete="username"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                data-testid="login-email"
                className="font-mono text-sm"
                placeholder="you@aielitegroup.com"
              />
            </div>
            <div className="space-y-2">
              <UILabel htmlFor="password" className="text-xs uppercase tracking-wider text-slate-600">Password</UILabel>
              <Input
                id="password"
                type="password"
                autoComplete="current-password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                data-testid="login-password"
              />
            </div>
            {error && (
              <div className="text-sm text-rose-700 bg-rose-50 border border-rose-200 rounded px-3 py-2" data-testid="login-error">
                {error}
              </div>
            )}
            <Button type="submit" disabled={loading} className="w-full bg-slate-900 hover:bg-slate-800 text-white h-11" data-testid="login-submit">
              {loading ? "Signing in…" : "Sign in"}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
}
